/**
 * Climatology options for marker colorization based on climate indicators
 */
export interface ClimatologyOptions {
    enabled: boolean;
    selectedIndicator: string | null;  // e.g., "climate_pr" (precipitation), "climate_tas" (mean temp), "climate_tasmax" (max temp), "climate_tasmin" (min temp) - null for vulnerability-profile-based
    timePeriod: TimePeriod;     // "historic", "near", "mid", "long"
    scenario: ScenarioSSP | null;  // SSP scenarios (null for historic period)
    vulnerabilityProfileId?: string;  // Vulnerability profile ID for consolidated risk score
    vulnerabilityProfileName?: string;  // Vulnerability profile type name, for the legend title (FR-1.40)
}

/**
 * Time periods for climatology projections
 */
export enum TimePeriod {
    NEAR = 'near',          // Near-term: 2021-2040
    MID = 'mid',            // Mid-century: 2041-2060  
    LONG = 'long',           // Long-term: 2081-2100
    HISTORIC = 'historic'  // Historical: 1995-2014
}

/**
 * Shared Socioeconomic Pathways (SSP) scenarios
 */
export enum ScenarioSSP {
    SSP119 = 'SSP119',
    SSP126 = 'SSP126',
    SSP245 = 'SSP245',
    SSP370 = 'SSP370',
    SSP585 = 'SSP585'
}

/**
 * Available climate indicators for colorization
 */
export enum ClimateIndicator {
    PRECIPITATION = 'climate_pr',                  // Precipitation
    MEAN_TEMPERATURE = 'climate_tas',              // Mean temperature
    MAX_TEMPERATURE = 'climate_tasmax',            // Maximum temperature
    MIN_TEMPERATURE = 'climate_tasmin',            // Minimum temperature
    SPI6 = 'climate_spi6',                        // Standardized Precipitation Index (6-month)
    COOLING_DEGREE_DAYS = 'climate_cdd',          // Cooling degree days
    TXX = 'climate_txx'                           // Maximum of daily maximum temperature
}

/**
 * Default climatology options
 */
export function getDefaultClimatologyOptions(): ClimatologyOptions {
    return {
        enabled: false,
        selectedIndicator: ClimateIndicator.PRECIPITATION,
        timePeriod: TimePeriod.NEAR,
        scenario: ScenarioSSP.SSP119
    };
}

/**
 * Transform climatology options to backend format
 */
export function transformClimatologyOptionsForBackend(options: ClimatologyOptions): any {
    // Remove "link_" prefix from selectedIndicator if present
    let indicator = options.selectedIndicator;
    if (indicator && indicator.startsWith('link_')) {
        indicator = indicator.substring(5); // Remove "link_" prefix
    }
    
    // Backend expects original time period values: "historic", "near", "mid", "long"
    // No transformation needed for timePeriod
    
    // Backend expects scenario as one of: "SSP119", "SSP126", "SSP245", "SSP370", "SSP585"
    // For historic period, scenario should be null/undefined
    let transformedScenario = null;
    if (options.scenario) {
        const scenario = options.scenario;
        const scenarioMap: { [key: string]: string } = {
            // Backward compatibility (older enum values)
            'SSP1-2.6': 'SSP119',
            'SSP2-4.5': 'SSP126',
            'SSP3-7.0': 'SSP245',
            'SSP4-6.0': 'SSP370',
            'SSP5-8.5': 'SSP585',
            // Backward compatibility (older backend codes)
            'ssp1': 'SSP119',
            'ssp2': 'SSP126',
            'ssp3': 'SSP245',
            'ssp4': 'SSP370',
            'ssp5': 'SSP585'
        };
        transformedScenario = scenarioMap[scenario] || scenario;
    }
    
    const result: any = {
        enabled: options.enabled,
        selectedIndicator: indicator,
        timePeriod: options.timePeriod,  // Keep original: "historic", "near", "mid", "long"
        scenario: transformedScenario
    };
    
    // Include vulnerability profile ID if present (for consolidated risk score)
    if (options.vulnerabilityProfileId) {
        result.vulnerabilityProfileId = options.vulnerabilityProfileId;
    }
    
    return result;
}
